import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import ShuffleIcon from "@material-ui/icons/Shuffle";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ReplayIcon from "@material-ui/icons/Replay";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import InboxIcon from "@material-ui/icons/Inbox";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AllInclusiveIcon from "@material-ui/icons/AllInclusive";
import EventIcon from "@material-ui/icons/Event";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import { useDialogStyles } from "../../theme/redesign";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { openBinancePaymentFromNotify } from "./BinanceCheckoutDialog";
import { toast } from "react-toastify";
import {
  accountAccessChanged,
  accountIsAutopay,
  accountIsNonRenewable,
  accountLiveStatus,
  accountOccupancy,
  accountStock,
  accountUsesPin,
  addDaysIso,
  daysLeft,
  formatDate,
  formatDateTime,
  flagEmoji,
  initialsOf,
  money,
  profileAccessChanged,
  publicFileUrl,
  pulseAccessUpdate,
  randomPin,
  pinLengthOf,
  statusColor,
  storedPhone,
  todayIso,
  visibleValue,
} from "./helpers";
import { needsSlots, saleTypeKey, countryNameOf, hasNoValidity, isMailbox, originCountryOf, productFilterLabel, productSiteUrl, productTitle, slotCapacity, slotKindOf, slotLabel, usesLoginPanel, usesMemberEmail, usesSiteLoginSlots } from "./catalog";
import { ContactAvatar, DialogClose, DueBadge, InfoDialog, PhoneFields, StatusChip, stopRow, tNegocio } from "./shared";
import { bumpNegocio, useNegocioLive } from "./Lists";
import { AccountDurationFields, AccountLicenseFields, AccountMoneyFields, AccountOriginFields, AccountPinFields, BillingTypePick, DualMoneyField, DURATION_PRESETS, accountFormExtras, applyBillingType, buildAccountPayload, emptyAccount } from "./AccountFields";
import { useStyles } from "./styles";
import EmailPickerField from "./EmailPicker";

const LIVE_STATUS_ICONS = {
  available: CheckCircleIcon,
  sold: ShoppingCartIcon,
  expiring: ScheduleIcon,
  expired: EventBusyIcon,
  fault: ReportProblemIcon,
  idle: PauseCircleOutlineIcon,
};

export const remainingLabel = (account) => {
  if (accountIsAutopay(account) || hasNoValidity(account)) return tNegocio("pages.accounts.billingType.unlimitedShort");
  const left = accountOccupancy(account).left;
  if (left == null) return "";
  if (left < 0) return tNegocio("pages.accounts.overdue");
  if (left === 1) return tNegocio("pages.accounts.remainingOne");
  return tNegocio("pages.accounts.remainingN").replace("{n}", String(left));
};

export function AccountLiveStatus({ account, compact = false }) {
  const classes = useStyles();
  const live = accountLiveStatus(account);
  const Icon = LIVE_STATUS_ICONS[live] || CheckCircleIcon;
  const color = statusColor(live);
  const label = tNegocio(`status.${live}`);
  return (
    <Tooltip title={label}>
      <span className={classes.liveStatus} style={{ color }}>
        <Icon style={{ fontSize: compact ? 18 : 18 }} />
        {compact ? null : <span>{label}</span>}
      </span>
    </Tooltip>
  );
}

export function AccountIdentity({ account, showStats = true }) {
  const classes = useStyles();
  const password = visibleValue(account?.password);
  const email = visibleValue(account?.email);
  const stock = accountStock(account);
  const orders = Number(account?.orders || 0);
  const isFault = accountLiveStatus(account) === "fault" || account?.status === "fault";
  const faultColor = isFault ? statusColor("fault") : undefined;
  return (
    <div className={classes.productHead} style={faultColor ? { color: faultColor } : undefined}>
      <ProductMark product={account?.product} circle />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 800, color: faultColor }}>{productTitle(account?.product)}</div>
        {originCountryOf(account) ? (
          <div className={classes.captionBlock} style={faultColor ? { color: faultColor } : undefined}>
            {flagEmoji(originCountryOf(account))} {countryNameOf(originCountryOf(account))}
          </div>
        ) : null}
        {accountIsNonRenewable(account) && <div style={{ fontSize: 11, fontWeight: 700, color: daysLeft(account.endDate) != null && daysLeft(account.endDate) < 0 ? "#b71c1c" : "#a65d00" }}>
          {daysLeft(account.endDate) != null && daysLeft(account.endDate) < 0 ? "No reutilizable · vencida" : "No renovable · un solo uso"}
        </div>}
        {email ? (
          <div className={classes.captionBlock} style={faultColor ? { color: faultColor, fontWeight: 600 } : undefined}>
            {email}
          </div>
        ) : null}
        {password ? (
          <div className={classes.accountPassword} style={faultColor ? { color: faultColor } : undefined}>
            <VpnKeyIcon style={{ fontSize: 13, color: faultColor }} />
            {password}
          </div>
        ) : null}
        {showStats ? (
          <div className={classes.accountMiniStats} style={faultColor ? { color: faultColor } : undefined}>
            <span>
              <InboxIcon style={{ fontSize: 13 }} /> {tNegocio("pages.accounts.stock")}: {stock}
            </span>
            <span>
              <ReceiptIcon style={{ fontSize: 13 }} /> {tNegocio("pages.accounts.orders")}: {orders}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const ProductMark = ({ product, size = 40, circle = false, link = true }) => {
  const classes = useStyles();
  const src = publicFileUrl(product?.imageUrl);
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  const href = link ? productSiteUrl(product) : "";
  const style = { width: size, height: size, borderRadius: circle ? "50%" : 10, cursor: href ? "pointer" : undefined };
  const inner =
    src && !broken ? (
      <img className={classes.productLogoImg} src={src} alt="" onError={() => setBroken(true)} />
    ) : (
      initialsOf(product?.name)
    );
  const mark = (
    <div
      className={classes.productLogo}
      style={style}
      title={href ? tNegocio("pages.products.openSite") : undefined}
    >
      {inner}
    </div>
  );
  if (!href) return mark;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ display: "inline-flex", flexShrink: 0, textDecoration: "none", color: "inherit" }}
    >
      {mark}
    </a>
  );
};

export const AccountMark = ({ account, email, slot }) => {
  const classes = useStyles();
  const text = email || account?.email || "—";
  return (
    <div className={classes.accountMark}>
      <span className={classes.accountMarkEmail}>{slot ? `${text} · ${slot}` : text}</span>
    </div>
  );
};

export const AccountOption = ({ account, email }) => {
  const classes = useStyles();
  const occ = accountOccupancy(account || {});
  const text = email || account?.email || "—";
  const left = occ.left == null ? "—" : occ.left;
  const free = occ.free;
  return (
    <span className={classes.accountMark}>
      <span className={classes.accountMarkEmail}>{text}</span>
      <span className={classes.accountOptionMeta}>
        ➥ {left} 📆 ➥ {free} 👤
      </span>
    </span>
  );
};

export async function fetchNegocioAccount(id) {
  if (!id) return null;
  const { data } = await api.get(`/negocio/accounts/${id}`);
  return data || null;
}

export function ProfileIcons({ account, onSelect, maxVisible = 6, compact = false, fillRow = false }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const wrapRef = useRef(null);
  const [allOpen, setAllOpen] = useState(false);
  const [fit, setFit] = useState(maxVisible);
  const occ = accountOccupancy(account || {});
  const profiles = [...(account?.profiles || [])].sort((a, b) => (a.slotNumber || 0) - (b.slotNumber || 0));
  const cap = slotCapacity(account) || Number(account?.slotCount || profiles.length || 0);
  const slots = occ.saleType === "full" ? 0 : Math.max(cap || 0, profiles.length);
  const items = occ.saleType === "full" ? [] : Array.from({ length: slots || profiles.length }, (_, i) => profiles[i] || null);
  useLayoutEffect(() => {
    if (!fillRow) return undefined;
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const w = el.clientWidth || 0;
      const slot = 46;
      const more = 40;
      const raw = Math.max(1, Math.floor(w / slot));
      const needsMore = items.length > raw;
      setFit(needsMore ? Math.max(1, Math.floor((w - more) / slot)) : raw);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillRow, items.length]);
  if (occ.saleType === "full") {
    return <StatusChip value="full" prefix="negocioModule.pages.accounts.saleTypes." />;
  }
  if (!items.length) {
    return <span className={classes.captionBlock}>{tNegocio("pages.accounts.noProfiles")}</span>;
  }
  const visible = fillRow ? fit : maxVisible;
  const extra = Math.max(0, items.length - visible);
  const shown = items.slice(0, visible);
  const renderSlot = (p, i, named = !compact) => {
    const busy = p && p.status !== "available";
    const name = p?.name || tNegocio("cols.profile");
    const siteLogin = usesSiteLoginSlots(account);
    const detail = siteLogin
      ? [p?.siteUser, p?.siteUrl].filter(Boolean).join(" · ")
      : p?.pin
        ? String(p.pin)
        : "";
    const label = p
      ? `${name}${detail ? ` · ${detail}` : ""} · ${
          busy ? tNegocio("pages.accounts.profileStatus.occupied") : tNegocio("pages.accounts.profileStatus.available")
        }`
      : "—";
    return (
      <Tooltip key={p?.id || `empty-${i}`} title={label}>
        <button
          type="button"
          className={`${classes.profileSlot} ${named ? "" : classes.profileSlotCompact} ${p ? classes.profileDotClick : ""}`}
          onClick={p && onSelect ? stopRow(() => { setAllOpen(false); onSelect(p); }) : undefined}
          disabled={!p || !onSelect}
        >
          <span className={`${classes.profileDot} ${busy ? classes.profileDotBusy : classes.profileDotFree}`}>
            <ContactAvatar
              name={name}
              number={p?.contact?.number}
              profilePicUrl={p?.profilePicUrl || p?.contact?.profilePicUrl}
              size={named ? 22 : 20}
            />
          </span>
          {named ? <span className={classes.profileSlotName}>{name}</span> : null}
        </button>
      </Tooltip>
    );
  };
  return (
    <>
      <div ref={wrapRef} className={fillRow ? classes.profileGridFill : undefined}>
        <div className={`${classes.profileGrid} ${compact ? classes.profileDotsCompact : ""} ${fillRow ? classes.profileGridFillInner : ""}`}>
          {shown.map((p, i) => renderSlot(p, i))}
          {extra ? (
            <Tooltip title={tNegocio("pages.accounts.seeAllProfiles")}>
              <button
                type="button"
                className={classes.profileMore}
                onClick={stopRow(() => setAllOpen(true))}
              >
                <MoreHorizIcon fontSize="small" />
                <span>+{extra}</span>
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <Dialog open={allOpen} onClose={() => setAllOpen(false)} maxWidth="sm" fullWidth classes={{ paper: dlg.paper }}>
        <DialogClose onClose={() => setAllOpen(false)} />
        <DialogTitle>{slotLabel(account, tNegocio, true)}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.profileDotsFill}>{items.map((p, i) => renderSlot(p, i, true))}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ProfileEditDialog({ profile, account, onClose, onSaved }) {
  const dlg = useDialogStyles();
  const siteLogin = usesSiteLoginSlots(account);
  const [form, setForm] = useState({
    name: "",
    pin: "",
    memberEmail: "",
    siteUrl: "",
    siteUser: "",
    sitePassword: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canDelete =
    !!profile?.id &&
    profile.status === "available" &&
    !profile.contactId;

  useEffect(() => {
    setForm({
      name: profile?.name || "",
      pin: profile?.pin || "",
      memberEmail: profile?.memberEmail || "",
      siteUrl: profile?.siteUrl || "",
      siteUser: profile?.siteUser || "",
      sitePassword: profile?.sitePassword || "",
    });
    setShowPass(false);
    setConfirmDelete(false);
  }, [profile]);

  const save = async () => {
    if (!profile?.id) return;
    if (siteLogin) {
      if (!String(form.siteUrl || "").trim()) {
        toast.error(tNegocio("fields.siteUrl"));
        return;
      }
      if (!String(form.siteUser || "").trim()) {
        toast.error(tNegocio("fields.siteUser"));
        return;
      }
      if (!String(form.sitePassword || "").trim()) {
        toast.error(tNegocio("fields.sitePassword"));
        return;
      }
    }
    const next = {
      name: form.name,
      pin: form.pin,
      memberEmail: form.memberEmail,
      siteUrl: form.siteUrl,
      siteUser: form.siteUser,
      sitePassword: form.sitePassword,
    };
    const accessChanged = profileAccessChanged(profile, next);
    setSaving(true);
    try {
      await api.put(`/negocio/profiles/${profile.id}`, {
        accountId: account?.id || profile.accountId,
        ...next,
        slotNumber: profile.slotNumber,
        status: profile.status,
        contactId: profile.contactId || null,
      });
      toast.success(tNegocio("toastSaved"));
      onSaved?.(
        {
          ...profile,
          ...next,
        },
        { accessChanged }
      );
      onClose?.();
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!profile?.id || !canDelete) return;
    setSaving(true);
    try {
      await api.delete(`/negocio/profiles/${profile.id}`);
      toast.success(tNegocio("toastSaved"));
      onSaved?.({ ...profile, _deleted: true });
      setConfirmDelete(false);
      onClose?.();
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={!!profile} onClose={onClose} maxWidth="xs" fullWidth classes={{ paper: dlg.paper }}>
        <DialogClose onClose={onClose} />
        <DialogTitle>{tNegocio("pages.accounts.editSlot")}</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("fields.name")}
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        {usesMemberEmail(account) ? (
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.memberEmail")}
            value={form.memberEmail}
            onChange={(e) => setForm((f) => ({ ...f, memberEmail: e.target.value }))}
          />
        ) : null}
        {siteLogin ? (
          <>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("fields.siteUrl")}
              value={form.siteUrl}
              onChange={(e) => setForm((f) => ({ ...f, siteUrl: e.target.value }))}
              helperText={tNegocio("fields.siteUrlHint")}
              placeholder="https://cliente.tudominio.com"
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("fields.siteUser")}
              value={form.siteUser}
              onChange={(e) => setForm((f) => ({ ...f, siteUser: e.target.value }))}
              helperText={tNegocio("fields.siteUserHint")}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type={showPass ? "text" : "password"}
              label={tNegocio("fields.sitePassword")}
              value={form.sitePassword}
              onChange={(e) => setForm((f) => ({ ...f, sitePassword: e.target.value }))}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPass((v) => !v)}>
                      {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        ) : null}
        {!siteLogin && (accountUsesPin(account) || visibleValue(form.pin)) ? (
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.pin")}
            value={form.pin}
            onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value }))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={tNegocio("pages.accounts.generatePin")}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          pin: randomPin(pinLengthOf(account, profile?.pin || f.pin)),
                        }))
                      }
                    >
                      <ShuffleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        ) : null}
        {!canDelete && profile?.status && profile.status !== "available" ? (
          <div style={{ marginTop: 8, opacity: 0.75, fontSize: 13 }}>
            {tNegocio("pages.accounts.deleteSlotBusy")}
          </div>
        ) : null}
      </DialogContent>
      <DialogActions>
        {canDelete ? (
          <Button color="secondary" disabled={saving} onClick={() => setConfirmDelete(true)} startIcon={<DeleteOutlineIcon />}>
            {tNegocio("pages.accounts.deleteSlot")}
          </Button>
        ) : null}
        <div style={{ flex: 1 }} />
        <Button color="primary" variant="contained" disabled={saving} onClick={save}>
          {tNegocio("save")}
        </Button>
      </DialogActions>
    </Dialog>
    <ConfirmationModal
      title={tNegocio("pages.accounts.deleteSlot")}
      open={confirmDelete}
      onClose={() => setConfirmDelete(false)}
      onConfirm={remove}
    >
      {tNegocio("pages.accounts.deleteSlotConfirm")}
    </ConfirmationModal>
    </>
  );
}

const accountFormFrom = (account) => ({
  id: account?.id,
  productId: account?.productId || account?.product?.id || "",
  status: account?.status || "available",
  saleType: account?.saleType || "profile",
  email: account?.email || "",
  password: "",
  startDate: account?.startDate || "",
  durationDays: account?.durationDays || 30,
  endDate: account?.endDate || "",
  notes: account?.notes || "",
  supplierId: account?.supplierId || "",
  customProfileName: !!account?.customProfileName,
  profileNamePrefix: account?.profileNamePrefix || "",
  ...accountFormExtras(account),
});

/** Formulario inicial: edición de una cuenta existente, o cuenta nueva (sin id) con los valores de Cuentas. */
const initialAccountForm = (account) =>
  Number(account?.id) > 0 ? accountFormFrom(account) : { ...emptyAccount(null), email: account?.email || "" };

export function AccountEditDialog({ open, account, onClose, onSaved, onAskUpdateNotify }) {
  const dlg = useDialogStyles();
  const creating = !(Number(account?.id) > 0);
  const [form, setForm] = useState(initialAccountForm(account));
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supplierDraft, setSupplierDraft] = useState(null);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const NEW_SUPPLIER = "__new_supplier__";

  const saveSupplierInline = async () => {
    if (!supplierDraft) return;
    const name = String(supplierDraft.name || "").trim();
    if (!name) {
      toast.error(tNegocio("fields.name"));
      return;
    }
    setSupplierSaving(true);
    try {
      const payload = {
        name,
        phone: storedPhone(supplierDraft.phone),
        telegram: String(supplierDraft.telegram || "").replace(/^@/, ""),
        notes: supplierDraft.notes || "",
      };
      const { data } = await api.post("/negocio/suppliers", payload);
      setSuppliers((prev) =>
        [...prev.filter((s) => Number(s.id) !== Number(data.id)), data].sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
        )
      );
      setForm((f) => ({ ...f, supplierId: data.id }));
      setSupplierDraft(null);
      bumpNegocio("suppliers");
      toast.success(tNegocio("toastSaved"));
    } catch (e) {
      toastError(e);
    } finally {
      setSupplierSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    setForm(initialAccountForm(account));
    setShowPass(false);
    let cancelled = false;
    (async () => {
      try {
        const [prod, sup] = await Promise.all([
          api.get("/negocio/products"),
          api.get("/negocio/suppliers").catch(() => ({ data: { records: [] } })),
        ]);
        if (cancelled) return;
        setProducts(prod.data.records || []);
        setSuppliers(sup.data.records || []);
      } catch (e) {
        toastError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, account]);

  const patch = (next) => setForm((current) => ({ ...current, ...next }));
  const selectedProduct = products.find((p) => Number(p.id) === Number(form.productId));
  const panelProduct = selectedProduct || form;
  const siteLoginAccount = usesSiteLoginSlots(panelProduct);

  const save = async () => {
    if (!creating && !form.id) return;
    if (!form.productId) {
      toast.error(tNegocio("fields.product"));
      return;
    }
    if (!String(form.email || "").trim()) {
      toast.error(siteLoginAccount ? tNegocio("fields.hostingAccount") : usesLoginPanel(panelProduct) ? tNegocio("fields.loginUser") : tNegocio("fields.email"));
      return;
    }
    if (form.saleType === "profile" && form.pinEnabled && form.pinCustomEnabled && !String(form.pinCustom || "").trim()) {
      toast.error(tNegocio("fields.pinCustom"));
      return;
    }
    if (creating && !siteLoginAccount && !String(form.password || "").trim()) {
      toast.error(tNegocio("fields.password"));
      return;
    }
    setSaving(true);
    if (creating) {
      try {
        const payload = buildAccountPayload(form);
        if (form.password) payload.password = form.password;
        const { data } = await api.post("/negocio/accounts", payload);
        toast.success(tNegocio("toastSaved"));
        bumpNegocio("accounts");
        onSaved?.(data, { created: true });
        onClose?.();
      } catch (e) {
        toastError(e);
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      const payload = buildAccountPayload(form);
      if (form.password) payload.password = form.password;
      const pinTouched =
        form.saleType === "profile" &&
        (!!form.pinEnabled !== !!account?.pinEnabled ||
          String(form.pinCustom || "") !== String(account?.pinCustom || "") ||
          Number(form.pinLength || 0) !== Number(account?.pinLength || 0));
      const accessChanged = accountAccessChanged(account, payload) || pinTouched;
      const { data } = await api.put(`/negocio/accounts/${form.id}`, payload);
      toast.success(tNegocio("toastSaved"));
      onSaved?.(data || { ...account, ...payload }, { accessChanged });
      onClose?.();
      if (accessChanged) onAskUpdateNotify?.(form.id);
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
      <DialogClose onClose={onClose} />
      <DialogTitle>{creating ? tNegocio("new.account") : tNegocio("edit")}</DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("fields.product")}
          value={form.productId || ""}
          onChange={(e) => {
            const product = products.find((p) => Number(p.id) === Number(e.target.value));
            if (creating) {
              // Cuenta nueva: valores del producto (como en Cuentas) sin perder lo ya escrito.
              setForm((f) => ({ ...emptyAccount(product), email: f.email, password: f.password, notes: f.notes, supplierId: f.supplierId }));
              return;
            }
            patch({
              productId: e.target.value,
              slotKind: product ? slotKindOf(product) : form.slotKind,
              fulfillmentType: product?.fulfillmentType || form.fulfillmentType,
              loginPanel: !!(product?.loginPanel || usesLoginPanel(product)),
              saleType: product && slotKindOf(product) === "none" ? "full" : form.saleType,
            });
          }}
        >
          {products.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <ProductMark product={p} size={22} circle />
                {productFilterLabel(p, tNegocio, money)}
              </span>
            </MenuItem>
          ))}
        </TextField>
        {creating && form.productId && needsSlots(products.find((p) => Number(p.id) === Number(form.productId)) || form) ? (
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.saleType")}
            value={form.saleType || "profile"}
            onChange={(e) => patch({ saleType: e.target.value })}
          >
            <MenuItem value="profile">
              {tNegocio(`pages.accounts.saleTypes.${saleTypeKey(products.find((p) => Number(p.id) === Number(form.productId)) || form)}`)}
            </MenuItem>
            <MenuItem value="full">{tNegocio("pages.accounts.saleTypes.full")}</MenuItem>
          </TextField>
        ) : null}
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("cols.status")}
          value={form.status || "available"}
          helperText={
            accountLiveStatus({ ...form, endDate: form.endDate, billingType: form.billingType }) === "expired" &&
            form.status === "available"
              ? tNegocio("pages.accounts.expiredBlocksAvailable")
              : undefined
          }
          onChange={(e) => {
            const status = e.target.value;
            patch({ status, ...(status !== "available" ? { botPublish: false } : {}) });
          }}
        >
          <MenuItem value="available" disabled={accountLiveStatus({ ...form, billingType: form.billingType }) === "expired" && form.status !== "available"}>
            {tNegocio("status.available")}
          </MenuItem>
          <MenuItem value="fault">{tNegocio("status.fault")}</MenuItem>
          <MenuItem value="idle">{tNegocio("status.idle")}</MenuItem>
          {form.status === "expired" || accountLiveStatus(form) === "expired" ? (
            <MenuItem value="expired">{tNegocio("status.expired")}</MenuItem>
          ) : null}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={form.status === "available" && form.botPublish !== false}
              disabled={form.status !== "available"}
              onChange={(e) => patch({ botPublish: e.target.checked })}
            />
          }
          label={tNegocio("pages.accounts.botPublish")}
        />
        {form.status !== "available" ? (
          <div style={{ marginBottom: 8, opacity: 0.75, fontSize: 13 }}>
            {tNegocio("pages.accounts.botPublishBlocked")}
          </div>
        ) : null}
        {siteLoginAccount || usesLoginPanel(panelProduct) ? (
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={siteLoginAccount ? tNegocio("fields.hostingAccount") : tNegocio("fields.loginUser")}
          value={form.email}
          onChange={(e) => patch({ email: e.target.value })}
          helperText={siteLoginAccount ? tNegocio("fields.hostingAccountHint") : tNegocio("fields.loginUserHint")}
        />
        ) : (
        <EmailPickerField label={tNegocio("fields.email")} value={form.email || ""} onChange={(email) => patch({ email })} />
        )}
        {!siteLoginAccount ? (
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          type={showPass ? "text" : "password"}
          label={tNegocio("fields.password")}
          value={form.password}
          onChange={(e) => patch({ password: e.target.value })}
          helperText={tNegocio("fields.passwordKeep")}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPass((v) => !v)}>
                  {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        ) : (
          <div style={{ marginBottom: 8, opacity: 0.75, fontSize: 13 }}>
            {tNegocio("pages.accounts.siteLoginHint")}
          </div>
        )}
        {usesLoginPanel(panelProduct) && !siteLoginAccount ? (
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.panelUrl")}
            value={form.url || ""}
            onChange={(e) => patch({ url: e.target.value })}
            helperText={tNegocio("fields.panelUrlHint")}
            placeholder="http://servidor:puerto"
          />
        ) : null}
        <AccountPinFields form={form} onChange={patch} showNames={false} />
        <AccountLicenseFields form={form} onChange={patch} />
        <AccountOriginFields form={form} onChange={patch} />
        <BillingTypePick value={form.billingType} onChange={(billingType) => patch(applyBillingType(form, billingType))} />
        <AccountDurationFields form={form} onChange={patch} />
        <AccountMoneyFields
          form={form}
          onChange={patch}
          slotCount={products.find((p) => Number(p.id) === Number(form.productId))?.maxProfiles || 0}
        />
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("fields.supplier")}
          value={form.supplierId || ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === NEW_SUPPLIER) {
              setSupplierDraft({ name: "", phone: "", telegram: "", notes: "" });
              return;
            }
            patch({ supplierId: v });
          }}
        >
          <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
          {suppliers.map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
          <MenuItem value={NEW_SUPPLIER}>
            <em>+ {tNegocio("new.supplier")}</em>
          </MenuItem>
        </TextField>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          multiline
          rows={2}
          label={tNegocio("fields.notes")}
          value={form.notes}
          onChange={(e) => patch({ notes: e.target.value })}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" disabled={saving} onClick={save}>
          {tNegocio("save")}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={!!supplierDraft}
      onClose={() => !supplierSaving && setSupplierDraft(null)}
      maxWidth="sm"
      fullWidth
      classes={{ paper: dlg.paper }}
    >
      <DialogClose onClose={() => !supplierSaving && setSupplierDraft(null)} disabled={supplierSaving} />
      <DialogTitle>{tNegocio("new.supplier")}</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("fields.name")}
          value={supplierDraft?.name || ""}
          onChange={(e) => setSupplierDraft((f) => ({ ...f, name: e.target.value }))}
        />
        <PhoneFields
          value={supplierDraft?.phone || ""}
          onChange={(phone) => setSupplierDraft((f) => ({ ...f, phone }))}
        />
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("fields.telegram")}
          value={supplierDraft?.telegram || ""}
          onChange={(e) => setSupplierDraft((f) => ({ ...f, telegram: e.target.value }))}
        />
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          multiline
          rows={2}
          label={tNegocio("fields.notes")}
          value={supplierDraft?.notes || ""}
          onChange={(e) => setSupplierDraft((f) => ({ ...f, notes: e.target.value }))}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" disabled={supplierSaving} onClick={saveSupplierInline}>
          {tNegocio("save")}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

function AccountSaleDialog(props) {
  const { PurchasedItemDialog } = require("./CartDialog");
  return <PurchasedItemDialog {...props} />;
}

export function AccountPeek({ open, accountId, account, onClose, onChanged, onEdit, onRenew, onAskUpdateNotify }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const history = useHistory();
  const [row, setRow] = useState(account || null);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saleItem, setSaleItem] = useState(null);
  const [barOn, setBarOn] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renew, setRenew] = useState(null);

  const reloadRow = React.useCallback(async (id) => {
    const found = await fetchNegocioAccount(id);
    if (found) setRow(found);
    return found;
  }, []);

  useNegocioLive(["accounts", "subscriptions", "sales"], () => {
    if (!open) return;
    const id = accountId || row?.id || account?.id;
    if (id) return reloadRow(id);
  });

  useEffect(() => {
    let cancelled = false;
    if (!open) {
      setRow(null);
      setProfile(null);
      setEditing(false);
      setSaleItem(null);
      setBarOn(false);
      setConfirmDelete(false);
      setRenew(null);
      return undefined;
    }
    setRow(account || null);
    const id = accountId || account?.id;
    if (!id) return undefined;
    (async () => {
      try {
        const found = await fetchNegocioAccount(id);
        if (!cancelled && found) setRow(found);
      } catch (e) {
        toastError(e);
      }
    })();
    const frame = requestAnimationFrame(() => setBarOn(true));
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [open, accountId, account, reloadRow]);

  const patchProfile = (updated, meta = {}) => {
    setRow((current) => {
      if (!current) return current;
      return {
        ...current,
        profiles: (current.profiles || []).map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
      };
    });
    if (meta.accessChanged) {
      const subs = (row?.subscriptions || []).filter(
        (s) => Number(s.profileId) === Number(updated.id) || (!s.profileId && Number(s.contactId) === Number(updated.contactId))
      );
      const ids = subs.map((s) => s.id);
      if (updated.contactId) pulseAccessUpdate({ contactId: updated.contactId, subscriptionIds: ids });
      else ids.forEach((id) => {
        const sub = subs.find((s) => Number(s.id) === Number(id));
        pulseAccessUpdate({ contactId: sub?.contactId, subscriptionId: id });
      });
      if (!ids.length && updated.contactId) pulseAccessUpdate({ contactId: updated.contactId });
    }
    onChanged?.();
  };

  const pulseAccountRecipients = async (accountId) => {
    const id = Number(accountId);
    if (!(id > 0)) return;
    try {
      const { data } = await api.get(`/negocio/accounts/${id}/update-recipients`);
      (data?.records || []).forEach((r) => {
        pulseAccessUpdate({ contactId: r.contactId, subscriptionId: r.subscriptionId });
      });
    } catch (_err) {
      /* sin destinatarios: el icono puede salir igual vía notifyPending al refrescar */
    }
  };

  const notifySale = async ({ kind, channel, contactId, subscriptionId }) => {
    try {
      const { data } = await api.post("/negocio/notify", { notify: true, kind, channel, contactId, subscriptionId });
      toast.success(tNegocio("pages.customers.sentOk"));
      openBinancePaymentFromNotify(data);
    } catch (e) {
      toastError(e);
    }
  };

  const occ = accountOccupancy(row || {});
  const autopay = accountIsAutopay(row);
  const duration = autopay || hasNoValidity(row) ? 0 : Number(row?.durationDays || 0);
  const remaining = occ.left == null ? 0 : Math.max(0, occ.left);
  const pct = duration ? Math.max(0, Math.min(100, (remaining / duration) * 100)) : 0;
  const ring = 2 * Math.PI * 34;
  const stock = accountStock(row);
  const orders = Number(row?.orders || (row?.subscriptions || []).length || 0);
  const leftover = row ? remainingLabel(row) : "";
  const details = [
    autopay || hasNoValidity(row)
      ? { label: tNegocio("pages.accounts.billingType.title"), value: tNegocio("pages.accounts.billingType.unlimitedShort") }
      : { label: tNegocio("cols.endDate"), value: formatDate(row?.endDate) },
    leftover ? { label: tNegocio("cols.remaining"), value: leftover } : null,
    visibleValue(row?.supplier?.name)
      ? {
          label: tNegocio("fields.supplier"),
          value: row.supplier.name,
          to: row.supplierId ? `/negocio/proveedores?supplierId=${row.supplierId}` : null,
        }
      : null,
    originCountryOf(row) ? { label: tNegocio("fields.originCountry"), value: `${flagEmoji(originCountryOf(row))} ${countryNameOf(originCountryOf(row))}` } : null,
    visibleValue(row?.product?.planLabel) ? { label: tNegocio("fields.planLabel"), value: row.product.planLabel } : null,
    row?.deviceLimit || row?.product?.deviceLimit ? { label: tNegocio("fields.deviceLimit"), value: String(row.deviceLimit || row.product.deviceLimit) } : null,
    row?.product?.warrantyDays ? { label: tNegocio("fields.warrantyDays"), value: String(row.product.warrantyDays) } : null,
    Number(row?.lotSize || 1) > 1 ? { label: tNegocio("fields.lotSize"), value: String(row.lotSize) } : null,
    visibleValue(row?.code) ? { label: tNegocio("cols.code"), value: row.code } : null,
    visibleValue(row?.url) ? { label: tNegocio("fields.serviceUrl"), value: row.url } : null,
    visibleValue(row?.ip) ? { label: tNegocio("fields.ip"), value: row.ip } : null,
    visibleValue(row?.notes) ? { label: tNegocio("fields.notes"), value: row.notes } : null,
  ].filter((field) => field && visibleValue(field.value));
  const sales = row?.subscriptions || [];
  const showPin = accountUsesPin(row);
  const leftDays = row ? daysLeft(row.endDate) : null;
  const canRenew = !accountIsNonRenewable(row) && !!(
    row &&
    !accountIsAutopay(row) &&
    !hasNoValidity(row) &&
    leftDays != null &&
    leftDays <= 3
  );
  const openRenew = (account) => {
    if (!account || accountIsNonRenewable(account)) return;
    const startDate = addDaysIso(account.endDate || todayIso(), 1);
    const durationDays = account.durationDays || 30;
    setRenew({
      id: account.id,
      email: account.email,
      product: account.product,
      startDate,
      durationDays,
      endDate: addDaysIso(startDate, durationDays),
      salePrice: account.salePrice || 0,
      salePriceFx: account.salePriceFx,
      saleCurrency: account.saleCurrency === "fx" ? "fx" : "local",
      cost: account.cost || 0,
      costFx: account.costFx,
      costCurrency: account.costCurrency === "fx" ? "fx" : "local",
    });
  };
  const patchRenew = (patch) => setRenew((current) => {
    const next = { ...current, ...patch };
    if (patch.startDate != null || patch.durationDays != null) {
      next.endDate = addDaysIso(next.startDate, next.durationDays);
    }
    return next;
  });
  const saveRenew = async () => {
    if (!renew?.id) return;
    try {
      await api.put(`/negocio/accounts/${renew.id}`, {
        renew: true,
        startDate: renew.startDate,
        durationDays: renew.durationDays,
        saleCurrency: renew.saleCurrency || "local",
        salePrice: renew.salePrice,
        salePriceFx: renew.salePriceFx,
        costCurrency: renew.costCurrency || "local",
        cost: renew.cost,
        costFx: renew.costFx,
      });
      toast.success(tNegocio("toastSaved"));
      setRenew(null);
      await reloadRow(renew.id);
      onChanged?.();
    } catch (e) {
      toastError(e);
    }
  };

  return (
    <>
      <Dialog open={open && !!row} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogClose onClose={onClose} />
        <DialogTitle disableTypography className={classes.accountPeekTitle}>
          <div>{tNegocio("pages.accounts.detail")}</div>
          <AccountLiveStatus account={row} />
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.accountHero}>
            <div className={classes.accountHeroTop}>
              <div className={classes.accountHeroLogoWrap}>
                <svg viewBox="0 0 80 80" className={classes.accountHeroRing}>
                  <circle cx="40" cy="40" r="34" className={classes.accountHeroRingTrack} />
                  <circle
                    cx="40"
                    cy="40"
                    r="34"
                    className={classes.accountHeroRingFill}
                    style={{
                      strokeDasharray: ring,
                      strokeDashoffset: barOn ? ring * (1 - pct / 100) : ring,
                    }}
                  />
                </svg>
                <ProductMark product={row?.product} size={56} circle />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className={classes.accountHeroName}>{row?.product?.name}</div>
                {visibleValue(row?.email) ? <div className={classes.accountHeroEmail}>{row.email}</div> : null}
                {visibleValue(row?.password) ? (
                  <div className={classes.accountHeroPass}>
                    <VpnKeyIcon style={{ fontSize: 14 }} />
                    {row.password}
                  </div>
                ) : null}
                {visibleValue(row?.code) ? <div className={classes.accountHeroCode}>{row.code}</div> : null}
              </div>
            </div>
            {autopay ? (
              <div className={classes.accountHeroDays}>
                <AllInclusiveIcon style={{ fontSize: 16 }} />
                {tNegocio("pages.accounts.billingType.unlimitedShort")}
              </div>
            ) : duration ? (
              <>
                <div className={classes.accountHeroDays}>
                  <EventIcon style={{ fontSize: 16 }} />
                  {tNegocio("pages.accounts.daysOf")
                    .replace("{left}", String(remaining))
                    .replace("{total}", String(duration))}
                </div>
                <div className={classes.accountHeroBar}>
                  <div className={classes.accountHeroBarFill} style={{ width: barOn ? `${pct}%` : "0%" }} />
                </div>
              </>
            ) : null}
            <div className={classes.accountHeroStats}>
              <div className={classes.accountHeroStat} style={{ animationDelay: "0.05s" }}>
                <InboxIcon style={{ fontSize: 18, color: "#0284c7" }} />
                <strong>{stock}</strong>
                <span>{tNegocio("pages.accounts.stock")}</span>
              </div>
              <div className={classes.accountHeroStat} style={{ animationDelay: "0.12s" }}>
                <ReceiptIcon style={{ fontSize: 18, color: "#db2777" }} />
                <strong>{orders}</strong>
                <span>{tNegocio("pages.accounts.orders")}</span>
              </div>
              <div className={classes.accountHeroStat} style={{ animationDelay: "0.2s" }}>
                {autopay ? <AllInclusiveIcon style={{ fontSize: 18, color: "#7c3aed" }} /> : <ScheduleIcon style={{ fontSize: 18, color: "#d97706" }} />}
                <strong>{autopay ? "∞" : remaining}</strong>
                <span>{tNegocio(autopay ? "pages.accounts.billingType.unlimitedShort" : "cols.remaining")}</span>
              </div>
            </div>
          </div>

          {details.map((field) => (
            <div key={field.label} className={classes.infoField}>
              <div className={classes.captionBlock}>{field.label}</div>
              {field.to ? (
                <button
                  type="button"
                  className={classes.clickableRow}
                  style={{ background: "none", border: 0, padding: 0, font: "inherit", fontWeight: 700, color: "inherit", cursor: "pointer", textAlign: "left" }}
                  onClick={() => history.push(field.to)}
                >
                  {field.value}
                </button>
              ) : (
                <div>{field.value}</div>
              )}
            </div>
          ))}

          {occ.saleType !== "full" ? (
            <>
              <div className={classes.sectionTitle}>{slotLabel(row, tNegocio, true)}</div>
              <ProfileIcons account={row} onSelect={setProfile} fillRow />
            </>
          ) : (
            <div className={classes.captionBlock} style={{ marginTop: 8 }}>
              {tNegocio(isMailbox(row) ? "pages.accounts.fullHintMailbox" : "pages.accounts.fullHint")}
            </div>
          )}

          <div className={classes.sectionTitle}>{tNegocio("pages.accounts.accountSales")}</div>
          {sales.length ? (
            sales.map((sub) => (
              <div
                key={sub.id}
                className={classes.accountSaleRow}
                onClick={() => setSaleItem(sub)}
                role="button"
                tabIndex={0}
              >
                <ContactAvatar
                  name={sub.contact?.name}
                  number={sub.contact?.number}
                  profilePicUrl={sub.contact?.profilePicUrl}
                  size={36}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className={classes.accountSaleName}>{sub.contact?.name || tNegocio("cols.contact")}</div>
                  <div className={classes.captionBlock}>
                    {[sub.profile?.name, showPin && visibleValue(sub.profile?.pin) ? `PIN ${sub.profile.pin}` : null]
                      .filter(Boolean)
                      .join(" · ") || sub.product?.name}
                  </div>
                </div>
                <DueBadge endDate={sub.endDate} status={sub.status} />
              </div>
            ))
          ) : (
            <div className={classes.captionBlock}>{tNegocio("pages.accounts.noSales")}</div>
          )}
        </DialogContent>
        <DialogActions>
          <div className={classes.actionCluster}>
            {canRenew ? (
              <Tooltip title={tNegocio("renew")}>
                <IconButton
                  size="small"
                  onClick={() => (onRenew ? onRenew(row) : openRenew(row))}
                  style={{ color: "#6a1b9a" }}
                >
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title={tNegocio("edit")}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  if (onEdit && row) onEdit(row);
                  else setEditing(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={tNegocio("pages.products.remove")}>
              <IconButton size="small" onClick={() => setConfirmDelete(true)} style={{ color: "#c62828" }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        title={tNegocio("confirmTitle")}
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          if (!row?.id) return;
          try {
            await api.delete(`/negocio/accounts/${row.id}`);
            toast.success(tNegocio("toastSaved"));
            setConfirmDelete(false);
            onClose();
            onChanged?.();
          } catch (e) {
            toastError(e);
          }
        }}
      >
        {tNegocio("confirmBody")}
      </ConfirmationModal>
      <Dialog open={!!renew && !onRenew} onClose={() => setRenew(null)} maxWidth="xs" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogClose onClose={() => setRenew(null)} />
        <DialogTitle>
          {tNegocio("renew")} · {renew?.product?.name || tNegocio("pages.accounts.title")}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.captionBlock} style={{ marginBottom: 8 }}>{renew?.email}</div>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="date"
            label={tNegocio("fields.startDate")}
            InputLabelProps={{ shrink: true }}
            value={renew?.startDate || ""}
            onChange={(e) => patchRenew({ startDate: e.target.value })}
          />
          <div className={classes.filters} style={{ marginBottom: 8 }}>
            {DURATION_PRESETS.map((days) => (
              <Button
                key={days}
                size="small"
                color={Number(renew?.durationDays) === days ? "primary" : "default"}
                variant={Number(renew?.durationDays) === days ? "contained" : "outlined"}
                onClick={() => patchRenew({ durationDays: days })}
              >
                {tNegocio(`pages.accounts.durations.${days}`)}
              </Button>
            ))}
          </div>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="number"
            label={tNegocio("fields.days")}
            value={renew?.durationDays ?? 30}
            onChange={(e) => patchRenew({ durationDays: +e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="date"
            label={tNegocio("fields.endDate")}
            InputLabelProps={{ shrink: true }}
            value={renew?.endDate || ""}
            InputProps={{ readOnly: true }}
          />
          <DualMoneyField
            label={tNegocio("pages.accounts.purchaseCost")}
            hint={tNegocio("pages.accounts.purchaseCostHint")}
            localValue={renew?.cost}
            fxValue={renew?.costFx}
            currency={renew?.costCurrency || "local"}
            onChange={({ local, fx, currency }) => patchRenew({ cost: local, costFx: fx, costCurrency: currency })}
          />
          <DualMoneyField
            label={tNegocio("pages.accounts.salePrice")}
            hint={tNegocio("pages.accounts.salePriceHint")}
            localValue={renew?.salePrice}
            fxValue={renew?.salePriceFx}
            currency={renew?.saleCurrency || "local"}
            onChange={({ local, fx, currency }) => patchRenew({ salePrice: local, salePriceFx: fx, saleCurrency: currency })}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={saveRenew}>{tNegocio("save")}</Button>
        </DialogActions>
      </Dialog>
      <AccountEditDialog
        open={editing}
        account={row}
        onClose={() => setEditing(false)}
        onSaved={async (_data, meta) => {
          setEditing(false);
          if (row?.id) await reloadRow(row.id);
          if (meta?.accessChanged) await pulseAccountRecipients(row?.id);
          onChanged?.();
        }}
        onAskUpdateNotify={(accountId) => {
          pulseAccountRecipients(accountId);
          onAskUpdateNotify?.(accountId);
        }}
      />
      <ProfileEditDialog
        profile={profile}
        account={row}
        onClose={() => setProfile(null)}
        onSaved={patchProfile}
      />
      <AccountSaleDialog
        open={!!saleItem}
        item={saleItem ? { ...saleItem, account: saleItem.account || row } : null}
        customer={{
          contactId: saleItem?.contactId || saleItem?.contact?.id,
          name: saleItem?.contact?.name,
          number: saleItem?.contact?.number,
          profilePicUrl: saleItem?.contact?.profilePicUrl,
        }}
        onClose={() => setSaleItem(null)}
        onPeekAccount={() => setSaleItem(null)}
        onChanged={async () => {
          if (row?.id) await reloadRow(row.id);
          onChanged?.();
        }}
        onNotify={notifySale}
      />
    </>
  );
}

const saleCustomer = (sale) => sale?.customerSnapshot || sale?.contact?.name || "—";
const saleService = (sale) => {
  if (sale?.serviceSnapshot) return sale.serviceSnapshot;
  const names = (sale?.subscriptions || []).map((sub) => sub.product?.name).filter(Boolean);
  return names.length ? [...new Set(names)].join(" + ") : "—";
};

export function SalePeek({ sale, onClose }) {
  return (
    <InfoDialog
      open={!!sale}
      onClose={onClose}
      title={sale ? saleCustomer(sale) : tNegocio("pages.sales.title")}
      subtitle={sale ? `#${sale.id} · ${formatDateTime(sale.createdAt)}` : ""}
      chips={
        sale ? (
          <>
            <StatusChip
              value={sale.status === "pending_payment" ? "pending_payment" : "sale"}
              prefix="negocioModule.pages.sales.types."
            />
            <StatusChip value={sale.status} />
          </>
        ) : null
      }
      fields={[
        { label: tNegocio("cols.contact"), value: sale ? saleCustomer(sale) : "" },
        { label: tNegocio("cols.phone"), value: sale?.contact?.number },
        { label: tNegocio("cols.service"), value: sale ? saleService(sale) : "" },
        { label: tNegocio("cols.payment"), value: sale?.paymentMethod?.name },
        { label: tNegocio("cols.total"), value: money(sale?.total) },
        { label: tNegocio("fields.notes"), value: sale?.notes },
      ]}
    />
  );
}
